/*
 * The indeterminate progress mark.
 *
 * Two shapes, chosen by whether a label is given. With a label it is a
 * `role="status"` carrying that label to a screen reader; without one it is
 * `aria-hidden` and purely decorative, which is what it must be inside a Button
 * that already announces `aria-busy` on itself. A spinner that announces
 * "loading" from inside a button that also announces it is busy says the same
 * thing twice, and the second time is the one that sounds like a bug.
 *
 * Under reduced motion the ring loses its gap rather than freezing mid-turn.
 * base.css suppresses animation globally, which would otherwise leave a
 * three-quarter arc stopped at an arbitrary angle, and a stopped spinner is the
 * universal sign that something has hung. A complete ring at reduced opacity
 * carries "in progress" without implying movement that is not happening.
 */

import styles from './Spinner.module.css'

export interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg'
  /**
   * The thing being waited for, in words. Omit it when the spinner sits inside
   * a control that already carries the announcement.
   */
  label?: string
  className?: string
}

export function Spinner({ size = 'md', label, className }: SpinnerProps) {
  const rootClassName = [
    styles.spinner,
    size === 'sm' ? styles.sm : '',
    size === 'lg' ? styles.lg : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  if (label === undefined) {
    return <span className={rootClassName} aria-hidden="true" />
  }

  return (
    <span className={styles.status} role="status">
      <span className={rootClassName} aria-hidden="true" />
      <span className="visually-hidden">{label}</span>
    </span>
  )
}
